import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Descriptions,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';
import { getTariffItemRowKey } from './tariffRowKeys';
import DashboardHero from '../../features/dashboard/components/DashboardHero';
import DashboardSection from '../../features/dashboard/components/DashboardSection';
import { TableSkeleton } from '../../features/dashboard/components/DashboardSkeleton';
import { ADMIN_REVIEW_MODAL_CLASS, hasMetricValue } from '../../features/dashboard/utils';
import './standard-tariff-dashboard.css';

const { Text } = Typography;

const getServerError = (e, fallback) =>
  (e?.response?.data?.message != null && e.response.data.message !== '')
    ? e.response.data.message
    : (e?.message || fallback);
const getServerMessage = (data, fallback) =>
  (data?.message != null && data.message !== '') ? data.message : fallback;

/**
 * StandardTariffDetail – Single HMO standard tariff (summary + items + edit scope).
 * Route: /admin/tariffs/standard/:tariffId
 */
const StandardTariffDetail = () => {
  const { tariffId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const stateTariff = location.state?.tariff;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tariff, setTariff] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsPageSize, setItemsPageSize] = useState(200);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [categoryId, setCategoryId] = useState(undefined);
  const [searchQ, setSearchQ] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [scopeOptions, setScopeOptions] = useState({ states: [], zones: [], facility_types: [], plans: [], locations: [] });
  const [scopeOptionsLoading, setScopeOptionsLoading] = useState(false);
  const [scopeModalOpen, setScopeModalOpen] = useState(false);
  const [scopeForm] = Form.useForm();
  const [scopeLoading, setScopeLoading] = useState(false);

  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [addItemLoading, setAddItemLoading] = useState(false);
  const [addItemForm] = Form.useForm();
  const [editPriceModalOpen, setEditPriceModalOpen] = useState(false);
  const [itemDetailRecord, setItemDetailRecord] = useState(null);
  const [editPriceLoading, setEditPriceLoading] = useState(false);
  const [editPriceItem, setEditPriceItem] = useState(null);
  const [editPriceForm] = Form.useForm();
  const [itemActionLoadingId, setItemActionLoadingId] = useState(null);

  const id = tariffId ?? tariff?.id ?? tariff?.tariff_id;
  const uploadReportPath =
    stateTariff?.upload_report_url ?? stateTariff?.upload_report_path ??
    tariff?.upload_report_url ?? tariff?.upload_report_path ??
    null;
  const hasUploadReport = Boolean(
    uploadReportPath ||
    (tariff && (tariff.has_upload_report || tariff.upload_report_available)) ||
    (stateTariff && (stateTariff.has_upload_report || stateTariff.upload_report_available))
  );

  const loadTariff = useCallback(async () => {
    if (!tariffId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await lib.getAdminStandardTariff();
      const body = resp?.data;
      if (body?.error) {
        setError((body?.message != null && body.message !== '') ? body.message : 'Unable to load tariff');
        setTariff(null);
      } else {
        const data = body?.data ?? body;
        const list = Array.isArray(data?.tariffs) ? data.tariffs : (data?.tariff ? [data.tariff] : []);
        if (list.length === 1 && data?.items_count != null) list[0].items_count = data.items_count;
        const match = list.find((t) => String(t.id ?? t.tariff_id) === String(tariffId));
        if (match) {
          setTariff(match);
        } else if (stateTariff && String(stateTariff.id ?? stateTariff.tariff_id) === String(tariffId)) {
          setTariff(stateTariff);
        } else {
          setTariff(list[0] || stateTariff || null);
          if (!list.length && !stateTariff) setError('Tariff not found');
        }
      }
    } catch (e) {
      setError(getServerError(e, 'Failed to load tariff'));
      setTariff(null);
    } finally {
      setLoading(false);
    }
  }, [tariffId, stateTariff]);

  const fetchItems = useCallback(async (page = 1, pageSize = 200) => {
    if (!id) return;
    try {
      const params = { standard_tariff_id: id, page, page_size: pageSize };
      if (categoryId != null && categoryId !== '') params.category_id = categoryId;
      if (searchQ && searchQ.trim()) params.q = searchQ.trim();
      const resp = await lib.getStandardTariff(params);
      const body = resp?.data;
      if (body?.error) {
        setItems([]);
        setItemsTotal(0);
        return;
      }
      const data = body?.data ?? body;
      const list = Array.isArray(data?.items) ? data.items : Array.isArray(data?.data?.items) ? data.data.items : [];
      setItems(list);
      const meta = data?.meta ?? data?.data?.meta;
      if (meta) {
        setItemsTotal(meta.total != null ? meta.total : (Array.isArray(list) ? list.length : 0));
        setItemsPage(meta.page != null ? meta.page : page);
        setItemsPageSize(meta.page_size != null ? meta.page_size : pageSize);
      } else {
        setItemsTotal(Array.isArray(list) ? list.length : 0);
        setItemsPage(page);
        setItemsPageSize(pageSize);
      }
    } catch {
      setItems([]);
      setItemsTotal(0);
    }
  }, [id, categoryId, searchQ]);

  useEffect(() => {
    if (stateTariff && String(stateTariff.id ?? stateTariff.tariff_id) === String(tariffId)) {
      setTariff(stateTariff);
      setLoading(false);
    } else {
      loadTariff();
    }
  }, [tariffId, stateTariff, loadTariff]);

  useEffect(() => {
    if (tariff) fetchItems(1, itemsPageSize);
    else setItems([]);
  }, [tariff, fetchItems, itemsPageSize]);

  useEffect(() => {
    lib.getTariffCategories()
      .then((resp) => {
        const body = resp?.data;
        const list = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
        setCategoryOptions(
          list
            .map((c) => ({ value: c.id ?? c.category_id, label: c.category_name ?? c.name ?? `Category ${c.id ?? c.category_id}` }))
            .filter((o) => o.value != null)
        );
      })
      .catch(() => setCategoryOptions([]));
  }, []);

  useEffect(() => {
    if (!scopeModalOpen) return;
    setScopeOptionsLoading(true);
    lib.getStandardTariffScopeOptions()
      .then((resp) => {
        const body = resp?.data;
        const data = (body?.error === 0 && body?.data) ? body.data : body ?? {};
        setScopeOptions({
          states: Array.isArray(data.states) ? data.states : [],
          zones: Array.isArray(data.zones) ? data.zones : [],
          facility_types: Array.isArray(data.facility_types) ? data.facility_types : [],
          plans: Array.isArray(data.plans) ? data.plans : [],
          locations: Array.isArray(data.locations) ? data.locations : [],
        });
      })
      .catch(() => setScopeOptions({ states: [], zones: [], facility_types: [], plans: [], locations: [] }))
      .finally(() => setScopeOptionsLoading(false));
  }, [scopeModalOpen]);

  const handleScopeSubmit = useCallback(async () => {
    if (!id) {
      notification.error({ message: 'No tariff to update', placement: 'topRight' });
      return;
    }
    try {
      const values = await scopeForm.validateFields();
      setScopeLoading(true);
      const body = {};
      if (values.scope_state != null && values.scope_state !== '') body.scope_state = values.scope_state;
      if (values.scope_zone_id != null && values.scope_zone_id !== '') body.scope_zone_id = values.scope_zone_id;
      if (values.scope_facility_type != null && values.scope_facility_type !== '') body.scope_facility_type = values.scope_facility_type;
      if (values.scope_location_id != null && values.scope_location_id !== '') body.scope_location_id = Number(values.scope_location_id) || values.scope_location_id;
      if (values.scope_plan_id != null && values.scope_plan_id !== '') body.scope_plan_id = Number(values.scope_plan_id) || values.scope_plan_id;
      if (values.effective_date) body.effective_date = dayjs(values.effective_date).format('YYYY-MM-DD');
      if (values.expiry_date) body.expiry_date = dayjs(values.expiry_date).format('YYYY-MM-DD');
      if (values.tariff_name != null && values.tariff_name !== '') body.tariff_name = values.tariff_name;
      if (values.is_active !== undefined) body.is_active = values.is_active;
      const resp = await lib.updateStandardTariff(id, body);
      if (resp?.data?.error) throw new Error(resp.data.message);
      setScopeModalOpen(false);
      loadTariff();
      notification.success({ message: getServerMessage(resp?.data, 'Scope updated'), placement: 'topRight' });
    } catch (e) {
      if (e?.errorFields) return;
      notification.error({ message: getServerError(e, 'Failed to update scope'), placement: 'topRight' });
    } finally {
      setScopeLoading(false);
    }
  }, [id, scopeForm, loadTariff]);

  const handleAddItemSubmit = useCallback(async () => {
    if (!id) return;
    try {
      const values = await addItemForm.validateFields();
      setAddItemLoading(true);
      const body = {
        item_name: values.item_name?.trim(),
        ...(values.category_id != null && values.category_id !== '' && { category_id: Number(values.category_id) }),
        ...(values.unit_cost != null && values.unit_cost !== '' && { unit_cost: Number(values.unit_cost) }),
        ...(values.facility_cost != null && values.facility_cost !== '' && { facility_cost: Number(values.facility_cost) }),
        ...(values.hmo_cost != null && values.hmo_cost !== '' && { hmo_cost: Number(values.hmo_cost) }),
        ...(values.item_brand?.trim() && { item_brand: values.item_brand.trim() }),
        ...(values.item_type?.trim() && { item_type: values.item_type.trim() }),
        ...(values.item_strength?.trim() && { item_strength: values.item_strength.trim() }),
        ...(values.item_presentation?.trim() && { item_presentation: values.item_presentation.trim() }),
        ...(values.item_dosage?.trim() && { item_dosage: values.item_dosage.trim() }),
      };
      const resp = await lib.addStandardTariffItem(id, body);
      if (resp?.data?.error) throw new Error(resp.data.message ?? 'Failed to add item');
      setAddItemModalOpen(false);
      addItemForm.resetFields();
      await fetchItems();
      await loadTariff();
      notification.success({ message: getServerMessage(resp?.data, 'Item added to standard tariff'), placement: 'topRight' });
    } catch (e) {
      if (e?.errorFields) return;
      notification.error({ message: getServerError(e, 'Failed to add item'), placement: 'topRight' });
    } finally {
      setAddItemLoading(false);
    }
  }, [id, addItemForm, fetchItems, loadTariff]);

  const openEditPriceModal = useCallback((record) => {
    setEditPriceItem(record);
    editPriceForm.setFieldsValue({
      unit_cost: record?.unit_cost != null ? Number(record.unit_cost) : undefined,
      facility_cost: record?.facility_cost != null ? Number(record.facility_cost) : undefined,
      hmo_cost: record?.hmo_cost != null ? Number(record.hmo_cost) : undefined,
    });
    setEditPriceModalOpen(true);
  }, [editPriceForm]);

  const handleEditPriceSubmit = useCallback(async () => {
    if (!id || !editPriceItem) return;
    const itemId = editPriceItem.tariff_item_id ?? editPriceItem.id;
    if (!itemId) return;
    try {
      const values = await editPriceForm.validateFields();
      setEditPriceLoading(true);
      const body = {};
      if (values.unit_cost != null && values.unit_cost !== '') body.unit_cost = Number(values.unit_cost);
      if (values.facility_cost != null && values.facility_cost !== '') body.facility_cost = Number(values.facility_cost);
      if (values.hmo_cost != null && values.hmo_cost !== '') body.hmo_cost = Number(values.hmo_cost);
      if (!Object.keys(body).length) {
        notification.warning({ message: 'Enter at least one price to update', placement: 'topRight' });
        return;
      }
      const resp = await lib.updateStandardTariffItem(id, itemId, body);
      if (resp?.data?.error) throw new Error(resp.data.message ?? 'Failed to update price');
      setEditPriceModalOpen(false);
      setEditPriceItem(null);
      editPriceForm.resetFields();
      await fetchItems(itemsPage, itemsPageSize);
      notification.success({ message: getServerMessage(resp?.data, 'Price updated on HMO standard tariff'), placement: 'topRight' });
    } catch (e) {
      if (e?.errorFields) return;
      notification.error({ message: getServerError(e, 'Failed to update price'), placement: 'topRight' });
    } finally {
      setEditPriceLoading(false);
    }
  }, [id, editPriceItem, editPriceForm, fetchItems, itemsPage, itemsPageSize]);

  const handleArchiveItem = useCallback((record) => {
    if (!id) return;
    const itemId = record?.tariff_item_id ?? record?.id;
    if (!itemId) return;
    Modal.confirm({
      title: 'Archive standard tariff item?',
      content: (
        <div>
          <p>
            Archive <strong>{record?.item_name ?? 'this item'}</strong> on the HMO standard tariff?
          </p>
          <Text type="secondary">
            This is a global archive. Facilities using this standard by reference will no longer see the item.
            Facility-specific tariff copies are not changed.
          </Text>
        </div>
      ),
      okText: 'Archive globally',
      okButtonProps: { danger: true },
      onOk: async () => {
        setItemActionLoadingId(itemId);
        try {
          const resp = await lib.archiveStandardTariffItem(id, itemId);
          if (resp?.data?.error) throw new Error(resp.data.message ?? 'Failed to archive item');
          await fetchItems(itemsPage, itemsPageSize);
          await loadTariff();
          notification.success({
            message: getServerMessage(resp?.data, 'Item archived on HMO standard tariff'),
            placement: 'topRight',
          });
        } catch (e) {
          notification.error({ message: getServerError(e, 'Failed to archive item'), placement: 'topRight' });
          throw e;
        } finally {
          setItemActionLoadingId(null);
        }
      },
    });
  }, [id, fetchItems, loadTariff, itemsPage, itemsPageSize]);

  const toOption = (o) => (o && typeof o === 'object' && ('value' in o || 'id' in o)
    ? { value: o.value ?? o.id, label: o.label ?? o.zone_name ?? o.category_name ?? o.name ?? String(o.value ?? o.id) }
    : { value: o, label: String(o) });
  const stateSelectOptions = (scopeOptions.states || []).map(toOption);
  const zoneSelectOptions = (scopeOptions.zones || []).map(toOption);
  const facilityTypeSelectOptions = (scopeOptions.facility_types || []).map(toOption);
  const planSelectOptions = (scopeOptions.plans || []).map(toOption);

  const featureEnabled =
    process.env.REACT_APP_TARIFF_FEATURE_ENABLED === '1' ||
    process.env.REACT_APP_TARIFF_FEATURE_ENABLED === 'true';

  // Standard tariff item layout (shared with the facility tariff page and the
  // facility portal): Item Name, Category, Strength, Presentation, Approved
  // Cost, Status, Actions. Code, source, brand and type open with the item.
  const itemColumns = [
    { title: 'Item Name', dataIndex: 'item_name', key: 'item_name', ellipsis: true, render: (v) => v ?? '—' },
    { title: 'Category', dataIndex: 'category_name', key: 'category', width: 140, render: (v) => v ?? '—' },
    { title: 'Strength', dataIndex: 'item_strength', key: 'item_strength', width: 110, ellipsis: true, render: (v) => (v && String(v).trim()) || '—' },
    { title: 'Presentation', dataIndex: 'item_presentation', key: 'item_presentation', width: 130, ellipsis: true, render: (v) => (v && String(v).trim()) || '—' },
    { title: 'Approved Cost', dataIndex: 'unit_cost', key: 'unit_cost', width: 120, align: 'right', render: (v) => (v != null ? Number(v) : '—') },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, r) => {
        const activeItem = r?.is_active == null ? true : Boolean(Number(r.is_active));
        return <Tag color={activeItem ? 'green' : 'default'}>{activeItem ? 'Active' : 'Archived'}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 170,
      render: (_, record) => {
        const itemId = record?.tariff_item_id ?? record?.id;
        return (
          <Space size="small" onClick={(e) => e.stopPropagation()}>
            <Button type="link" size="small" onClick={() => openEditPriceModal(record)}>
              Edit price
            </Button>
            <Button
              type="link"
              size="small"
              danger
              loading={itemActionLoadingId === itemId}
              onClick={() => handleArchiveItem(record)}
            >
              Archive
            </Button>
          </Space>
        );
      },
    },
  ];

  const tariffName = tariff?.tariff_name ?? tariff?.name ?? 'Tariff';
  const active = tariff?.is_active != null ? Boolean(tariff.is_active) : null;
  const hasActiveFilters = Boolean(categoryId || (searchQ && searchQ.trim()));

  const heroMetrics = useMemo(() => {
    if (!tariff) return [];
    return [
      {
        key: 'items',
        title: 'Line items',
        value: itemsTotal || tariff.items_count || items.length,
        color: '#3497D3',
        icon: <UnorderedListOutlined />,
        subtitle: hasActiveFilters ? 'Matching filters' : 'In this tariff',
      },
      {
        key: 'page',
        title: 'On this page',
        value: items.length,
        color: '#722ed1',
        icon: <DollarOutlined />,
        subtitle: `Page ${itemsPage}`,
      },
      {
        key: 'status',
        title: 'Status',
        value: active === true ? 'Active' : active === false ? 'Inactive' : '—',
        color: active ? '#49B870' : '#94a3b8',
        icon: active ? <CheckCircleOutlined /> : <CloseCircleOutlined />,
        subtitle: tariff.effective_date ? `From ${new Date(tariff.effective_date).toLocaleDateString()}` : 'Effective date',
      },
      {
        key: 'category',
        title: 'Category',
        value: tariff.category_name ?? (tariff.category_id != null ? `Category ${tariff.category_id}` : '—'),
        color: '#faad14',
        icon: <DollarOutlined />,
        subtitle: tariff.expiry_date ? `Expires ${new Date(tariff.expiry_date).toLocaleDateString()}` : 'No expiry',
      },
    ].filter((metric) => hasMetricValue(metric.value) || metric.key === 'page');
  }, [tariff, itemsTotal, items.length, itemsPage, active, hasActiveFilters]);

  const openScopeModal = () => {
    scopeForm.setFieldsValue({
      tariff_name: tariff?.tariff_name ?? tariff?.name ?? undefined,
      scope_state: tariff?.scope_state ?? undefined,
      scope_zone_id: tariff?.scope_zone_id ?? undefined,
      scope_facility_type: tariff?.scope_facility_type ?? undefined,
      scope_location_id: tariff?.scope_location_id ?? undefined,
      scope_plan_id: tariff?.scope_plan_id ?? undefined,
      effective_date: tariff?.effective_date ? dayjs(tariff.effective_date) : undefined,
      expiry_date: tariff?.expiry_date ? dayjs(tariff.expiry_date) : undefined,
      is_active: tariff?.is_active != null ? !!tariff.is_active : true,
    });
    setScopeModalOpen(true);
  };

  if (!featureEnabled) {
    return (
      <div className="standard-tariff-disabled">
        <Alert
          type="info"
          message="Tariff module is currently disabled"
          action={<Button type="link" onClick={() => navigate('/dashboard')}>Back to dashboard</Button>}
        />
      </div>
    );
  }

  return (
    <div className="standard-tariff-dashboard">
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <h1 className="dash-masthead__title">{tariffName}</h1>
            <p className="dash-masthead__meta">
              <Button type="link" size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/tariffs')} style={{ padding: 0, height: 'auto' }}>
                Standard tariffs
              </Button>
              <span className="dash-masthead__sep" aria-hidden>·</span>
              Standard tariff details and line items
            </p>
          </div>
        </header>

        {tariff ? (
          <section className="standard-tariff-overview dash-overview dash-overview--compact" aria-label="Tariff summary">
            <DashboardHero metrics={heroMetrics} loading={loading && !items.length} />
          </section>
        ) : null}

        {error ? (
          <Alert type="error" showIcon closable message="Error loading tariff" description={error} onClose={() => setError(null)} />
        ) : null}

        {loading && !tariff ? (
          <div className="standard-tariff-loading">
            <Spin size="large" />
          </div>
        ) : tariff ? (
          <>
            <DashboardSection
              id="standard-tariff-profile"
              icon={<DollarOutlined />}
              title="Tariff profile"
              subtitle="Scope, dates, and registry metadata"
              collapsible={false}
              variant="content"
              action={(
                <Button size="small" icon={<EditOutlined />} onClick={openScopeModal} disabled={!tariff}>
                  Edit scope
                </Button>
              )}
            >
              <div className="standard-tariff-summary-grid">
                <div className="standard-tariff-summary-item">
                  <span className="standard-tariff-summary-item__label">Name</span>
                  <span className="standard-tariff-summary-item__value">{tariff.tariff_name ?? tariff.name ?? '—'}</span>
                </div>
                <div className="standard-tariff-summary-item">
                  <span className="standard-tariff-summary-item__label">Items</span>
                  <span className="standard-tariff-summary-item__value">{tariff.items_count != null ? Number(tariff.items_count).toLocaleString() : '—'}</span>
                </div>
                <div className="standard-tariff-summary-item">
                  <span className="standard-tariff-summary-item__label">Effective date</span>
                  <span className="standard-tariff-summary-item__value">{tariff.effective_date ? new Date(tariff.effective_date).toLocaleDateString() : '—'}</span>
                </div>
                <div className="standard-tariff-summary-item">
                  <span className="standard-tariff-summary-item__label">Expiry date</span>
                  <span className="standard-tariff-summary-item__value">{tariff.expiry_date ? new Date(tariff.expiry_date).toLocaleDateString() : '—'}</span>
                </div>
                <div className="standard-tariff-summary-item">
                  <span className="standard-tariff-summary-item__label">Active</span>
                  <span className="standard-tariff-summary-item__value">{tariff.is_active != null ? (tariff.is_active ? 'Yes' : 'No') : '—'}</span>
                </div>
                {(tariff.scope_state != null || tariff.scope_zone_id != null || tariff.scope_facility_type != null || tariff.scope_plan_id != null) ? (
                  <>
                    <div className="standard-tariff-summary-item">
                      <span className="standard-tariff-summary-item__label">Scope state</span>
                      <span className="standard-tariff-summary-item__value">{tariff.scope_state ?? '—'}</span>
                    </div>
                    <div className="standard-tariff-summary-item">
                      <span className="standard-tariff-summary-item__label">Scope zone</span>
                      <span className="standard-tariff-summary-item__value">{tariff.scope_zone_id ?? '—'}</span>
                    </div>
                    <div className="standard-tariff-summary-item">
                      <span className="standard-tariff-summary-item__label">Facility type</span>
                      <span className="standard-tariff-summary-item__value">{tariff.scope_facility_type ?? '—'}</span>
                    </div>
                    <div className="standard-tariff-summary-item">
                      <span className="standard-tariff-summary-item__label">Plan</span>
                      <span className="standard-tariff-summary-item__value">{tariff.scope_plan_id ?? '—'}</span>
                    </div>
                  </>
                ) : null}
              </div>
            </DashboardSection>

            <DashboardSection
              id="standard-tariff-items"
              icon={<UnorderedListOutlined />}
              title="Tariff items"
              subtitle={`${itemsTotal || items.length}${tariff.items_count != null ? ` of ${tariff.items_count}` : ''} line items`}
              collapsible={false}
              variant="content"
              action={(
                <div className="standard-tariff-toolbar standard-tariff-toolbar--inline">
                  <Input
                    className="standard-tariff-toolbar__search"
                    size="small"
                    placeholder="Search items…"
                    prefix={<SearchOutlined />}
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    onPressEnter={() => {
                      setItemsPage(1);
                      fetchItems(1, itemsPageSize);
                    }}
                    allowClear
                  />
                  <Select
                    className="standard-tariff-toolbar__select"
                    size="small"
                    placeholder="Category"
                    allowClear
                    value={categoryId}
                    onChange={setCategoryId}
                    options={categoryOptions}
                  />
                  <Button
                    size="small"
                    onClick={() => {
                      setSearchQ('');
                      setCategoryId(undefined);
                      setItemsPage(1);
                      fetchItems(1, itemsPageSize);
                    }}
                    disabled={!hasActiveFilters}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                      setItemsPage(1);
                      fetchItems(1, itemsPageSize);
                    }}
                  >
                    Search
                  </Button>
                  {hasUploadReport && uploadReportPath ? (
                    <Button
                      size="small"
                      onClick={async () => {
                        try {
                          await lib.downloadFileWithAuth(uploadReportPath);
                          notification.success({ message: 'Upload report download started', placement: 'topRight' });
                        } catch (e) {
                          notification.error({
                            message: 'Failed to download upload report',
                            description: e?.message || 'Please try again.',
                            placement: 'topRight',
                          });
                        }
                      }}
                    >
                      Download report
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={() => { loadTariff(); fetchItems(itemsPage, itemsPageSize); }}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setAddItemModalOpen(true)}
                    disabled={!tariff}
                  >
                    Add item
                  </Button>
                </div>
              )}
            >
              {loading && !items.length ? (
                <TableSkeleton rows={8} columns={5} />
              ) : items.length === 0 ? (
                <Empty description={hasActiveFilters ? 'No items match the current filters' : 'No items in this tariff'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div className="standard-tariff-table-wrap">
                  <Table
                    size="small"
                    dataSource={items}
                    columns={itemColumns}
                    rowKey={getTariffItemRowKey}
                    onRow={(record) => ({
                      onClick: () => setItemDetailRecord(record),
                      style: { cursor: 'pointer' },
                    })}
                    pagination={{
                      current: itemsPage,
                      pageSize: itemsPageSize,
                      total: itemsTotal,
                      showSizeChanger: true,
                      pageSizeOptions: ['50', '100', '200'],
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                      onChange: (page, pageSize) => {
                        setItemsPage(page);
                        setItemsPageSize(pageSize);
                        fetchItems(page, pageSize);
                      },
                      onShowSizeChange: (page, pageSize) => {
                        setItemsPage(page);
                        setItemsPageSize(pageSize);
                        fetchItems(page, pageSize);
                      },
                    }}
                  />
                </div>
              )}
            </DashboardSection>
          </>
        ) : !error ? (
          <Empty description="Tariff not found. It may have been removed or the ID is invalid." />
        ) : null}
      </div>

      <Modal
        title="Item details"
        open={!!itemDetailRecord}
        onCancel={() => setItemDetailRecord(null)}
        footer={[
          <Button key="edit" onClick={() => { const r = itemDetailRecord; setItemDetailRecord(null); openEditPriceModal(r); }}>Edit price</Button>,
          <Button key="close" type="primary" onClick={() => setItemDetailRecord(null)}>Close</Button>,
        ]}
        width={520}
        destroyOnClose
      >
        {itemDetailRecord && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Item name">{itemDetailRecord.item_name ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Category">{itemDetailRecord.category_name ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Strength">{itemDetailRecord.item_strength || '—'}</Descriptions.Item>
            <Descriptions.Item label="Presentation">{itemDetailRecord.item_presentation || '—'}</Descriptions.Item>
            <Descriptions.Item label="Type">{itemDetailRecord.item_type || '—'}</Descriptions.Item>
            <Descriptions.Item label="Dosage">{itemDetailRecord.item_dosage || '—'}</Descriptions.Item>
            <Descriptions.Item label="Brand">{itemDetailRecord.item_brand || '—'}</Descriptions.Item>
            <Descriptions.Item label="Code">{itemDetailRecord.item_code || '—'}</Descriptions.Item>
            <Descriptions.Item label="Source">{itemDetailRecord.item_source === 'upload' ? 'Upload' : itemDetailRecord.item_source === 'costable' ? 'Costable' : (itemDetailRecord.item_source || '—')}</Descriptions.Item>
            <Descriptions.Item label="Approved Cost">{itemDetailRecord.unit_cost != null ? Number(itemDetailRecord.unit_cost) : '—'}</Descriptions.Item>
            <Descriptions.Item label="Facility cost">{itemDetailRecord.facility_cost != null ? Number(itemDetailRecord.facility_cost) : '—'}</Descriptions.Item>
            <Descriptions.Item label="HMO cost">{itemDetailRecord.hmo_cost != null ? Number(itemDetailRecord.hmo_cost) : '—'}</Descriptions.Item>
            <Descriptions.Item label="Effective">{itemDetailRecord.effective_date || '—'}</Descriptions.Item>
            <Descriptions.Item label="Expiry">{itemDetailRecord.expiry_date || '—'}</Descriptions.Item>
            <Descriptions.Item label="Status"><Tag color={(itemDetailRecord.is_active == null || Number(itemDetailRecord.is_active)) ? 'green' : 'default'}>{(itemDetailRecord.is_active == null || Number(itemDetailRecord.is_active)) ? 'Active' : 'Archived'}</Tag></Descriptions.Item>
            <Descriptions.Item label="Last updated">{itemDetailRecord.updated_at || itemDetailRecord.created_at || '—'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title="Edit scope / dates"
        open={scopeModalOpen}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        onCancel={() => setScopeModalOpen(false)}
        onOk={handleScopeSubmit}
        confirmLoading={scopeLoading}
        okText="Save"
        width={520}
        destroyOnClose
      >
        <Form form={scopeForm} layout="vertical">
          <Form.Item name="tariff_name" label="Tariff name">
            <Input placeholder="e.g. HMO Standard 2024" />
          </Form.Item>
          {scopeOptionsLoading ? (
            <Form.Item label="Scope options"><Spin size="small" /></Form.Item>
          ) : (
            <>
              <Form.Item name="scope_state" label="Scope state">
                <Select placeholder="Pick state" allowClear options={stateSelectOptions} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="scope_zone_id" label="Scope zone">
                <Select placeholder="Pick zone" allowClear options={zoneSelectOptions} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="scope_facility_type" label="Scope facility type">
                <Select placeholder="Pick facility type" allowClear options={facilityTypeSelectOptions} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="scope_location_id" label="Scope location ID">
                <Input placeholder="e.g. 123" allowClear />
              </Form.Item>
              <Form.Item name="scope_plan_id" label="Scope plan">
                <Select placeholder="Pick plan" allowClear options={planSelectOptions} style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}
          <Form.Item name="effective_date" label="Effective date">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="expiry_date" label="Expiry date">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="is_active" valuePropName="checked" label="Active">
            <Checkbox>Tariff is active</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add tariff item"
        open={addItemModalOpen}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        onCancel={() => { setAddItemModalOpen(false); addItemForm.resetFields(); }}
        footer={[
          <Button key="cancel" onClick={() => { setAddItemModalOpen(false); addItemForm.resetFields(); }}>Cancel</Button>,
          <Button key="submit" type="primary" loading={addItemLoading} onClick={handleAddItemSubmit}>Add item</Button>,
        ]}
        width={520}
        destroyOnClose
      >
        <Form form={addItemForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="item_name" label="Item name" rules={[{ required: true, message: 'Item name is required' }]}>
            <Input placeholder="e.g. Paracetamol 500mg" />
          </Form.Item>
          <Form.Item name="category_id" label="Category">
            <Select allowClear placeholder="Select category" options={categoryOptions} />
          </Form.Item>
          <Form.Item name="unit_cost" label="Unit cost">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="Optional" />
          </Form.Item>
          <Form.Item name="facility_cost" label="Facility cost">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="Optional" />
          </Form.Item>
          <Form.Item name="hmo_cost" label="HMO cost">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="Optional" />
          </Form.Item>
          <Form.Item name="item_brand" label="Brand">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="item_type" label="Type">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="item_dosage" label="Dosage">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="item_strength" label="Strength">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="item_presentation" label="Presentation">
            <Input placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editPriceItem ? `Edit price — ${editPriceItem.item_name ?? 'item'}` : 'Edit price'}
        open={editPriceModalOpen}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        onCancel={() => {
          setEditPriceModalOpen(false);
          setEditPriceItem(null);
          editPriceForm.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setEditPriceModalOpen(false);
              setEditPriceItem(null);
              editPriceForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={editPriceLoading} onClick={handleEditPriceSubmit}>
            Save prices
          </Button>,
        ]}
        width={440}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Updates prices on the HMO standard tariff (global). Facilities using this standard by reference will see the new prices.
        </Text>
        <Form form={editPriceForm} layout="vertical">
          <Form.Item name="unit_cost" label="Unit cost" rules={[{ required: true, message: 'Unit cost is required' }]}>
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="facility_cost" label="Facility cost">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="Optional" />
          </Form.Item>
          <Form.Item name="hmo_cost" label="HMO cost">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StandardTariffDetail;
